import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import dataService from '../../data/dataService';
import '../../styles/Admin.css';
import '../../styles/DiscountAdmin.css';

interface DiscountCode {
  id: number;
  code: string;
  discountPercent: number;
  isActive: boolean;
  createdAt: string;
  expiresAt: string;
  usageCount: number;
}

const DiscountCodes: React.FC = () => {
  const [codes, setCodes] = useState<DiscountCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newPercent, setNewPercent] = useState('10');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editPercent, setEditPercent] = useState('');

  const load = () => {
    setLoading(true);
    dataService.getDiscountCodes()
      .then(setCodes)
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    const pct = parseFloat(newPercent);
    if (isNaN(pct) || pct <= 0 || pct > 100) return;
    setCreating(true);
    await dataService.createDiscountCode(pct);
    setCreating(false);
    setNewPercent('10');
    load();
  };

  const handleInvalidate = async (id: number) => {
    await dataService.invalidateDiscountCode(id);
    load();
  };

  const handleExtend = async (id: number) => {
    await dataService.extendDiscountCode(id);
    load();
  };

  const handleUpdatePercent = async (id: number) => {
    const pct = parseFloat(editPercent);
    if (isNaN(pct) || pct <= 0 || pct > 100) return;
    await dataService.updateDiscountCode(id, pct);
    setEditingId(null);
    setEditPercent('');
    load();
  };

  const isExpired = (d: DiscountCode) => new Date(d.expiresAt) < new Date();
  const getStatus = (d: DiscountCode) => {
    if (!d.isActive) return 'inactive';
    if (isExpired(d)) return 'expired';
    return 'active';
  };

  const daysLeft = (d: DiscountCode) => {
    const diff = new Date(d.expiresAt).getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  if (loading) return <div className="admin-page"><p>Loading discount codes...</p></div>;

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>Discount Codes</h1>
        <nav className="admin-nav">
          <Link to="/admin">Dashboard</Link>
          <Link to="/admin/orders">Orders</Link>
          <Link to="/admin/products">Products</Link>
          <Link to="/admin/reviews">Reviews</Link>
          <Link to="/admin/agent-dashboard">Agent Demo</Link>
        </nav>
      </div>

      {/* Create New Code */}
      <div className="discount-create-card">
        <h2>Generate New Code</h2>
        <div className="discount-create-row">
          <div className="discount-create-field">
            <label>Discount %</label>
            <input
              type="number"
              min="1"
              max="100"
              value={newPercent}
              onChange={(e) => setNewPercent(e.target.value)}
              placeholder="10"
            />
          </div>
          <div className="discount-create-field">
            <label>Expires</label>
            <input type="text" value="14 days from now" readOnly className="readonly-field" />
          </div>
          <button className="discount-generate-btn" onClick={handleCreate} disabled={creating}>
            {creating ? 'Generating...' : '🎟️ Generate Code'}
          </button>
        </div>
      </div>

      {/* Codes Table */}
      <div className="admin-section">
        <h2>All Codes ({codes.length})</h2>
        {codes.length === 0 ? (
          <p className="empty-state">No discount codes yet. Generate one above.</p>
        ) : (
          <table className="admin-table discount-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Discount</th>
                <th>Status</th>
                <th>Expires</th>
                <th>Used</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {codes.map(d => (
                <tr key={d.id} className={getStatus(d) !== 'active' ? 'row-dimmed' : ''}>
                  <td>
                    <code className="discount-code-display">{d.code}</code>
                  </td>
                  <td>
                    {editingId === d.id ? (
                      <span className="edit-inline">
                        <input
                          type="number"
                          min="1"
                          max="100"
                          value={editPercent}
                          onChange={(e) => setEditPercent(e.target.value)}
                          className="edit-percent-input"
                        />
                        <button className="btn-save" onClick={() => handleUpdatePercent(d.id)}>✓</button>
                        <button className="btn-cancel" onClick={() => setEditingId(null)}>✕</button>
                      </span>
                    ) : (
                      <span className="percent-display" onClick={() => { setEditingId(d.id); setEditPercent(String(d.discountPercent)); }}>
                        {d.discountPercent}%
                        <span className="edit-hint">✏️</span>
                      </span>
                    )}
                  </td>
                  <td>
                    <span className={`status-badge status-${getStatus(d)}`}>
                      {getStatus(d) === 'active' && `Active · ${daysLeft(d)}d left`}
                      {getStatus(d) === 'expired' && 'Expired'}
                      {getStatus(d) === 'inactive' && 'Deactivated'}
                    </span>
                  </td>
                  <td>{new Date(d.expiresAt).toLocaleDateString()}</td>
                  <td>{d.usageCount ?? 0}</td>
                  <td className="action-cell">
                    {d.isActive && !isExpired(d) && (
                      <button className="btn-action btn-invalidate" onClick={() => handleInvalidate(d.id)}>
                        Deactivate
                      </button>
                    )}
                    {(isExpired(d) || !d.isActive) && (
                      <button className="btn-action btn-extend" onClick={() => handleExtend(d.id)}>
                        Extend +14d
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default DiscountCodes;
